import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from '@material-ui/core'
import { Button } from 'components/facets'
import { TablePaginationControls } from 'components/facets/TablePaginationControls'
import { getFullTransactionsData } from 'lib/api'
import { getPriceFormat } from 'lib/utils'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table } from 'react-virtualized'
import {
  ChannelFilterOptions,
  formatDateToYYYYMMDD,
  getDisplayLocationName,
  getNMonthsAgo,
  TAXATION_SCHEME_CONVERSIONS,
} from 'shared'
import { TransactionLogInterface } from '../inventory/InventoryTransactionsPresenter'
import { Filter } from '../stockPricing/FilterDock/Filter'
import {
  getFormattedDate,
  listRowsPerPage,
  SortDirection,
  sortDirectionOptions,
} from './BackofficePresenter'

export interface InventoryTransactionsSearchProps {
  userId: string | undefined
  selectedPtProduct: number | undefined
}

export const InventoryTransactionsSearch = ({
  userId,
  selectedPtProduct,
}: InventoryTransactionsSearchProps): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const copyLastError = async (e: React.MouseEvent, lastError: string) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(lastError)
      enqueueSnackbar('Last error copied to clipboard', { variant: 'success' })
    } catch {
      enqueueSnackbar('Failed to copy last error', { variant: 'error' })
    }
  }
  const [loading, setLoading] = useState(false)

  const defaultFilterValues = {
    processedAtFrom: getNMonthsAgo(12),
    processedAtTo: new Date(),
    impersonatinUserId: userId,
    channel: '' as ChannelFilterOptions | '',
    ptProductId: selectedPtProduct,
    size: 25,
    page: 0,
  }
  const [processedAtFrom, setProcessedAtFrom] = useState<Date>(defaultFilterValues.processedAtFrom)
  const [processedAtTo, setProcessedAtTo] = useState<Date>(defaultFilterValues.processedAtTo)

  const [currentPage, setCurrentPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState(listRowsPerPage[0])
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [selectedChannel, setSelectedChannel] = useState<ChannelFilterOptions | ''>(
    defaultFilterValues.channel
  )

  const [totalPages, setTotalPages] = useState<number>()
  const [totalPagesCapped, setTotalPagesCapped] = useState(false)
  const [transactions, setTransactions] = useState<TransactionLogInterface[]>([])
  const [hasSearched, setHasSearched] = useState(false)

  const channelOptions = [
    <MenuItem key="all" value="">
      {t('filters.any')}
    </MenuItem>,
    ...Object.values(ChannelFilterOptions).map((channel) => (
      <MenuItem key={channel} value={channel}>
        {t(`common.channels.${channel}`)}
      </MenuItem>
    )),
  ]

  const handleSearchTransactions = async (page = currentPage) => {
    if (!userId) {
      enqueueSnackbar('select user before search transactions', { variant: 'error' })
      return
    }
    setLoading(true)

    const params = {
      processedAtFrom: processedAtFrom,
      processedAtTo: processedAtTo,
      impersonatingUserId: defaultFilterValues.impersonatinUserId,
      channel: selectedChannel || Object.values(ChannelFilterOptions).join(','),
      ptProductId: defaultFilterValues.ptProductId,
      sortDirection,
      size: rowsPerPage,
      page,
    }

    const response = await getFullTransactionsData(params)
    setTransactions(response.data)
    setTotalPages(Math.ceil(response.totalCount / rowsPerPage))
    setTotalPagesCapped(response.totalCountCapped)
    setHasSearched(true)
    setLoading(false)
  }

  const handleSearchWithReset = () => {
    setCurrentPage(0)
    handleSearchTransactions(0)
  }

  // Mounted and re-run by picking an article; another article has its own page count, so the
  // search starts from its first page
  useEffect(() => {
    handleSearchWithReset()
  }, [selectedPtProduct])

  const renderTaxationFromTo = (transaction: any) => {
    // tax rows are the only ones carrying a tax delta; everything else leaves this column empty
    if (transaction.taxDeltaQuantity === null || transaction.taxDeltaQuantity === undefined) {
      return <div></div>
    }

    const toScheme = TAXATION_SCHEME_CONVERSIONS.find(
      (item) => item.code === transaction.taxationScheme
    )?.label
    const fromScheme = TAXATION_SCHEME_CONVERSIONS.find(
      (item) => item.code !== transaction.taxationScheme
    )?.label

    return (
      <Box textAlign="center">
        <div className="d-flex flex-column align-items-center">
          {transaction.taxationScheme !== null && transaction.taxationScheme !== undefined && (
            <span>
              {fromScheme} &rArr; {toScheme}
            </span>
          )}
          <span>
            {transaction.taxQuantityFrom} &rArr; {transaction.taxQuantityTo} (
            {transaction.taxDeltaQuantity > 0 ? '+' : ''}
            {transaction.taxDeltaQuantity})
          </span>
        </div>
      </Box>
    )
  }

  const renderPriceFromTo = (transaction: any) => {
    if (
      (transaction.priceFrom === 0 || transaction.priceFrom === null) &&
      (transaction.priceTo === 0 || transaction.priceTo === null)
    ) {
      return <div></div>
    } else
      return (
        <Box textAlign="center">
          {getPriceFormat(transaction.priceFrom || 0, transaction.currency)} &rArr;{' '}
          {getPriceFormat(transaction.priceTo || 0, transaction.currency)} (
          {(transaction.priceTo || 0) - (transaction.priceFrom || 0) >= 0 ? '+' : ''}
          {getPriceFormat(
            (transaction.priceTo || 0) - (transaction.priceFrom || 0),
            transaction.currency
          )}
          )
        </Box>
      )
  }

  // header (40) + one row per transaction (45), capped so long lists stay scrollable
  const tableHeight = Math.min(transactions.length * 45 + 40, 460)

  return (
    <>
      <h4 className="my-4">InventoryTransactions</h4>
      <Grid container spacing={3}>
        <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
          <Filter
            type="select"
            menuitems={channelOptions}
            setFilter={(_name, newValue) => setSelectedChannel(newValue)}
            defaultValue={selectedChannel}
            label={t(`common.channel`)}
            name={'channel'}
            activeFilter={false}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
          <Box>
            <TextField
              fullWidth
              label="Processed at - from"
              type="date"
              defaultValue={formatDateToYYYYMMDD(processedAtFrom)}
              onChange={(e) => setProcessedAtFrom(new Date(e.target.value))}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
          <Box>
            <TextField
              fullWidth
              label="Processed at - to"
              type="date"
              defaultValue={formatDateToYYYYMMDD(processedAtTo)}
              onChange={(e) => setProcessedAtTo(new Date(e.target.value))}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
          <Filter
            type="select"
            menuitems={sortDirectionOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
            setFilter={(_name, newValue) => setSortDirection(newValue)}
            defaultValue={sortDirection}
            label="Sort"
            name={'sort'}
            activeFilter={false}
          />
        </Grid>

        <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
          <Filter
            type="select"
            menuitems={listRowsPerPage.map((row) => (
              <MenuItem key={row} value={row}>
                {row}
              </MenuItem>
            ))}
            setFilter={(_name, newValue) => setRowsPerPage(newValue)}
            defaultValue={listRowsPerPage[0]}
            label="Rows"
            name={'rows'}
            activeFilter={false}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
          <Button
            data-testid="search-transactions-btn"
            variant="contained"
            disabled={!userId}
            onClick={() => handleSearchWithReset()}
          >
            Search Transactions
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <div className="w-100 d-flex flex-column align-items-center ">
          <h6 className="pb-5">Loading</h6>
          <CircularProgress size={100} />
        </div>
      ) : transactions.length === 0 ? (
        hasSearched && (
          <span className="my-5 d-block text-center fw-bold" style={{ fontSize: '1.1rem' }}>
            No search results found
          </span>
        )
      ) : (
        <>
          <div className="transactions-table my-5" style={{ height: tableHeight + 30 }}>
            <span className="mb-1">{transactions.length} rows</span>
            <AutoSizer>
              {({ width }) => (
                <Table
                  width={Math.max(width, 1300)}
                  height={tableHeight}
                  headerHeight={40}
                  rowHeight={45}
                  rowCount={transactions.length}
                  rowGetter={({ index }) => transactions[index]}
                  className={`users-table`}
                >
                  <Column
                    width={120}
                    flexGrow={1}
                    flexShrink={1}
                    label={t('common.channel')}
                    dataKey="channel"
                    cellRenderer={({ rowData: transaction }) => <span>{transaction.channel}</span>}
                  />
                  <Column
                    width={150}
                    flexGrow={1}
                    flexShrink={1}
                    label={t('transaction.createdAt')}
                    dataKey="createdAt"
                    disableSort={true}
                    cellRenderer={({ rowData: transaction }) => (
                      <span className="text-wrap">{getFormattedDate(transaction.createdAt)}</span>
                    )}
                  />

                  <Column
                    width={170}
                    flexGrow={1}
                    flexShrink={1}
                    label={t('transaction.processedAt')}
                    dataKey="processedAt"
                    cellRenderer={({ rowData: transaction }) => (
                      <Box width="150px">
                        {' '}
                        {transaction.processedAt === null ? (
                          <>
                            <Tooltip title="Failed" placement="top">
                              <span className="processed-icon failed-icon">F</span>
                            </Tooltip>
                            <span className="text-wrap">
                              {getFormattedDate(transaction.lastFailedAt)}
                            </span>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Success" placement="top">
                              <span className="processed-icon success-icon">S</span>
                            </Tooltip>
                            <span className="text-wrap">
                              {getFormattedDate(transaction.processedAt)}
                            </span>
                          </>
                        )}
                      </Box>
                    )}
                  />
                  <Column
                    width={200}
                    flexGrow={2}
                    flexShrink={1}
                    label="Last error"
                    dataKey="last error"
                    cellRenderer={({ rowData: transaction }) => (
                      <div className="d-flex align-items-center">
                        <div
                          className="text-clamp-2"
                          title={transaction.lastError}
                          style={{ width: '100px', whiteSpace: 'normal' }}
                        >
                          {transaction.lastError}
                        </div>
                        {transaction.lastError && (
                          <IconButton
                            size="small"
                            title="Copy last error"
                            onClick={(e) => copyLastError(e, transaction.lastError)}
                          >
                            <i className="fa-light fa-copy" />
                          </IconButton>
                        )}
                      </div>
                    )}
                  />
                  <Column
                    width={120}
                    flexGrow={1}
                    flexShrink={1}
                    label={t('transaction.cause')}
                    dataKey="cause"
                    cellRenderer={({ rowData: transaction }) => (
                      <Box width="100px" className="text-wrap text-break">
                        {transaction.cause}
                      </Box>
                    )}
                  />
                  <Column
                    width={120}
                    flexGrow={1}
                    flexShrink={1}
                    label={t('transaction.source')}
                    dataKey="source"
                    cellRenderer={({ rowData: transaction }) => (
                      <Box width="100px">{transaction.source}</Box>
                    )}
                  />
                  <Column
                    width={160}
                    flexGrow={1}
                    flexShrink={1}
                    label={t('card.location')}
                    dataKey="location"
                    cellRenderer={({ rowData: transaction }) => (
                      <div>
                        <Tooltip
                          title={getDisplayLocationName(transaction.location)}
                          placement="top"
                        >
                          <Box
                            width="150px"
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {getDisplayLocationName(transaction.location)}
                          </Box>
                        </Tooltip>
                      </div>
                    )}
                  />
                  <Column
                    width={130}
                    flexGrow={1}
                    flexShrink={1}
                    label={'Quantity from-to'}
                    dataKey="fromTo"
                    cellRenderer={({ rowData: transaction }) => (
                      <Box width="100px" textAlign="center">
                        {transaction.quantityFrom !== null &&
                        transaction.quantityFrom !== undefined ? (
                          <>
                            {transaction.quantityFrom} &rArr; {transaction.quantityTo} (
                            {transaction.deltaQuantity > 0 ? '+' : ''}
                            {transaction.deltaQuantity})
                          </>
                        ) : (
                          <></>
                        )}
                      </Box>
                    )}
                  />
                  <Column
                    width={130}
                    flexGrow={1}
                    flexShrink={1}
                    label={'Reserved from-to'}
                    dataKey="reservedFromTo"
                    cellRenderer={({ rowData: transaction }) => (
                      <Box width="100px" textAlign="center">
                        {transaction.reservedQuantityFrom != null &&
                        transaction.reservedQuantityFrom != undefined ? (
                          <>
                            {transaction.reservedQuantityFrom} &rArr;{' '}
                            {transaction.reservedQuantityTo} (
                            {transaction.reservedDeltaQuantity > 0 ? '+' : ''}
                            {transaction.reservedDeltaQuantity}){' '}
                          </>
                        ) : (
                          <></>
                        )}
                      </Box>
                    )}
                  />

                  <Column
                    width={160}
                    flexGrow={1}
                    flexShrink={1}
                    label="Price from-to"
                    dataKey="priceFromTo"
                    headerClassName="centered-header"
                    cellRenderer={({ rowData: transaction }) => (
                      <Box className="text-wrap">{renderPriceFromTo(transaction)}</Box>
                    )}
                  />

                  <Column
                    width={200}
                    flexGrow={1}
                    flexShrink={1}
                    label="Taxation from-to"
                    dataKey="taxationFromTo"
                    headerClassName="centered-header"
                    cellRenderer={({ rowData: transaction }) => (
                      <Box className="text-wrap">{renderTaxationFromTo(transaction)}</Box>
                    )}
                  />

                  <Column
                    width={140}
                    flexGrow={0}
                    flexShrink={0}
                    label="Source Order id"
                    dataKey="orderId"
                    cellRenderer={({ rowData: transaction }) => <Box>{transaction.orderId}</Box>}
                  />
                </Table>
              )}
            </AutoSizer>
          </div>
          <TablePaginationControls
            currentPage={currentPage}
            setPage={(page) => {
              setCurrentPage(page)
              handleSearchTransactions(page)
            }}
            isLastPage={!totalPagesCapped && currentPage + 1 >= (totalPages ?? 0)}
            totalPages={totalPages}
            totalPagesCapped={totalPagesCapped}
            startFromZero={true}
          />
        </>
      )}
    </>
  )
}
